import { Message } from "./message-list";
import ResponseRenderer from "./response-renderer";
import MessageActions from "./message-actions";
import AddMessageChatMemoButton from "./add-message-chat-memo-button";
import AiAgentNotEnoughCredit from "../ai/ai-agent-not-enough-credit";
import AnswerFeedbackDialog from "./answer-feedback-dialog";
import Pipeline from "./pipeline";
import { overlay } from "overlay-kit";
import { toast } from "sonner";
import { useAiAgentStore } from "@/stores/ai-agent";
import { useLocation } from "react-router-dom";
import { useDemoChatStreamMutation } from "@/hooks/services/mutations/ai-agent/use-demo-chat-stream-mutation";
import ReferenceButton from "./reference-button";
import useUserQuery from "@/hooks/services/queries/user/use-user-query";
import GenerateReportButton from "../ai/generate-report-button";

export interface ResponseBlock {
  type:
  | "markdown"
  | "related-job"
  | "generate-report"
  | "answer-reference"
  | "paper-titles"
  | "paper-markdown";
  content?: string;
  metadata?: any;
}

const AssistantMessage = ({
  message,
  previousMessage,
  isLastMessage,
  messageIndex,
}: {
  message: Message;
  messageIndex: number;
  previousMessage: Message;
  isLastMessage: boolean;
}) => {
  const { data: userData } = useUserQuery();
  const getChatSourceSettings = useAiAgentStore((state) => state.getChatSourceSettings);
  const isThisChatStreaming = useAiAgentStore(
    (state) => state.streamingChatIds.has(message.chatId)
  );
  const isShared = useLocation().pathname.includes("share");
  const { mutate: sendStreamMessage } = useDemoChatStreamMutation(
    message.chatId
  );
  // 채팅방별 progressList (사고과정 데이터)
  const progressList = useAiAgentStore((state) => state.getProgressList(message.chatId));
  const isShowingPipeline =
    isThisChatStreaming && message.chatType === "DeepResearch" && progressList.length > 0;

  const handleLikeClick = () => {
    overlay.open(({ isOpen, close }) => (
      <AnswerFeedbackDialog
        isOpen={isOpen}
        onClose={() => close()}
        isLiked={true}
        chatId={message.chatId}
        chatHistoryId={message.id}
      />
    ));
  };

  const handleUnlikeClick = () => {
    overlay.open(({ isOpen, close }) => (
      <AnswerFeedbackDialog
        isOpen={isOpen}
        onClose={() => close()}
        isLiked={false}
        chatId={message.chatId}
        chatHistoryId={message.id}
      />
    ));
  };

  const handleCopyClick = () => {
    const refs = message.referencesContent ?? [];
    const text = message.message.replace(/\[(\d+)\]/g, (match, num) => {
      const ref = refs.find((r) => r.reference_number === Number(num));
      return ref?.url ? `${match}(${ref.url})` : match;
    });
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleRefreshClick = () => {
    if (isThisChatStreaming) return;
    const settings = getChatSourceSettings(message.chatId);
    sendStreamMessage({
      chatId: message.chatId,
      newMessage: previousMessage.message,
      deepResearch: settings.deepResearch,
      source: {
        web_search_enabled: settings.webSearchEnabled,
        private_kb_enabled: settings.privateKbEnabled,
        biomcp_enabled: settings.biomcpEnabled,
        paper_search_preferred: settings.academicJournalEnabled,
      },
    });
  };

  return (
    <div className="w-full overflow-x-visible">
      {/* Pipeline 표시: progressList가 있거나 chatType이 DeepResearch인 경우 */}
      {isShowingPipeline && isLastMessage && <Pipeline chatId={message.chatId} />}
      <div className="prose prose-slate max-w-none">
        <ResponseRenderer
          content={message.message.replace(/\n---\s*$/,  "")}
          references={message?.referencesContent}
          /* 본문 [A]/[RDB] 태그가 열 Citation 데이터 */
          knowledgeContent={message.knowledgeContent ?? null}
          messageKey={String(message.id ?? message.conversationTime)}
        />
      </div>
      {/*
        스트리밍 응답 시작 대기 상태 표시
        조건: 마지막 메시지 && 어시스턴트 메시지 && 내용 비어있음
        - message.message === "" 자체가 스트리밍 대기 상태를 의미함
          (에러 시 onError에서 빈 메시지 정리, 완료 시 content 채워짐)
      */}
      {isLastMessage &&
        message.messageType === "ASSISTANT" && message.message === "" ? (
        <>
          {!isShowingPipeline && (
            <div className="flex items-center gap-3">
              <div className="flex justify-center items-center w-12 h-12">
                <div className="w-8 h-8 border-4 border-primary-80 border-t-primary-40 rounded-full animate-spin"></div>
              </div>
              <span className="text-grey-20 text-base font-light">
                관련 정보 검색 중입니다 ...
              </span>
            </div>
          )}
        </>
      ) : (
        <>
          {message.isGenerateFinalReport && (
              
              <div className="py-4">
                <GenerateReportButton message={message} />
              </div>
            )}
          {(!isLastMessage || !isThisChatStreaming) && (
            <>
              <hr className="border-grey-80 my-4 w-full" />
              <div className="flex items-center gap-[24px]">
                <div className="flex items-center gap-5">
                  {!isShared && (
                    <AddMessageChatMemoButton
                      chatId={message.chatId}
                      chatHistoryId={message.id}
                      messageIndex={messageIndex}
                    />
                  )}
                  <MessageActions
                    onCopyClick={handleCopyClick}
                    onRefreshClick={handleRefreshClick}
                    onLikeClick={handleLikeClick}
                    onUnlikeClick={handleUnlikeClick}
                  />
                </div>

                {/* 웹 출처도 KB 자료도 없으면 버튼이 스스로 숨는다 */}
                <ReferenceButton
                  references={message?.referencesContent}
                  knowledgeContent={message.knowledgeContent}
                  instanceKey={message.id}
                />
              </div>
            </>
          )}
        </>
      )}

      {userData?.role === 1 &&
        isLastMessage &&
        userData?.availableCredit < 1 && (
          <div className="py-5">
            <AiAgentNotEnoughCredit />
          </div>
        )}
    </div>
  );
};

export default AssistantMessage;
